import api from '@/services/api';
import type { PaginatedResponse } from '@/types/api';
import type {
  LeadProvider,
  LeadProviderType,
  ImportBatch,
  LeadImport,
  ImportSummary,
  QualificationResult,
  ImportStatus,
  LeadScoreClassification,
  BatchStatus,
} from '@/types/leadImport';

/**
 * Lead import system API service (admin).
 */
export const leadImportService = {
  // ── Providers ──────────────────────────────────────────────

  /**
   * Lists all lead providers.
   */
  async getProviders(): Promise<LeadProvider[]> {
    const { data } = await api.get<LeadProvider[]>('/lead-providers');
    return data;
  },

  /**
   * Creates a new lead provider.
   */
  async createProvider(payload: {
    name: string;
    type: LeadProviderType;
    config?: string;
  }): Promise<LeadProvider> {
    const { data } = await api.post<LeadProvider>('/lead-providers', payload);
    return data;
  },

  /**
   * Updates an existing lead provider.
   */
  async updateProvider(
    id: string,
    payload: { name: string; config?: string; isActive: boolean },
  ): Promise<LeadProvider> {
    const { data } = await api.put<LeadProvider>(`/lead-providers/${id}`, payload);
    return data;
  },

  // ── Batches ────────────────────────────────────────────────

  /**
   * Lists import batches with pagination and optional status filter.
   */
  async getBatches(params?: {
    page?: number;
    limit?: number;
    status?: BatchStatus;
  }): Promise<PaginatedResponse<ImportBatch>> {
    const { data } = await api.get<PaginatedResponse<ImportBatch>>(
      '/import-batches',
      { params },
    );
    return data;
  },

  // ── Imports ────────────────────────────────────────────────

  /**
   * Uploads a CSV file for a given provider.
   */
  async uploadCsv(file: File, providerId: string): Promise<ImportSummary> {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await api.post<ImportSummary>(
      `/lead-imports/upload?providerId=${providerId}`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return data;
  },

  /**
   * Lists imported leads with pagination and filters.
   */
  async getImports(params?: {
    page?: number;
    limit?: number;
    status?: ImportStatus;
    batchId?: string;
    classification?: LeadScoreClassification;
  }): Promise<PaginatedResponse<LeadImport>> {
    const { data } = await api.get<PaginatedResponse<LeadImport>>(
      '/lead-imports',
      { params },
    );
    return data;
  },

  /**
   * Gets a single import by its identifier.
   */
  async getImportById(id: string): Promise<LeadImport> {
    const { data } = await api.get<LeadImport>(`/lead-imports/${id}`);
    return data;
  },

  /**
   * Processes (qualifies) a single import.
   */
  async processImport(id: string): Promise<LeadImport> {
    const { data } = await api.post<LeadImport>(`/lead-imports/${id}/process`);
    return data;
  },

  /**
   * Converts a qualified import into a lead.
   */
  async convertImport(id: string): Promise<LeadImport> {
    const { data } = await api.post<LeadImport>(`/lead-imports/${id}/convert`);
    return data;
  },

  /**
   * Processes all imports in a batch (bulk qualification).
   */
  async processBatch(batchId: string): Promise<QualificationResult> {
    const { data } = await api.post<QualificationResult>(
      `/lead-imports/batch/${batchId}/process`,
    );
    return data;
  },

  /**
   * Downloads the CSV import template.
   */
  async downloadTemplate(): Promise<Blob> {
    const { data } = await api.get<Blob>('/lead-imports/template', {
      responseType: 'blob',
    });
    return data;
  },
};
